import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import CountUp from '../../../components/ui/CountUp';
import { Gift, Star, CalendarClock, DollarSign, Search, ChevronLeft, ChevronRight, User, History as HistoryIcon, Plus, CheckCircle2, ArrowRight } from 'lucide-react';
import type { WhatsAppClient } from '../../../types/user';
import type { Reward } from '../types/reward';
import { useError } from '../../../context/ErrorContext';
import SearchInput, { SearchResultItem } from '../../../components/common/SearchInput';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import { rewardsApi } from '../../../api/rewardsApi';
import { clientsApi } from '../../../api/clientsApi';
import { membershipsApi, membershipCovers, type MembershipSummary } from '../../../api/membershipsApi';

const REWARD_IMG_FALLBACK = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' rx='8' fill='%23e5e7eb'/%3E%3Cpath d='M12 18h16v10a2 2 0 01-2 2H14a2 2 0 01-2-2zM10 14h20v4H10zM20 14v16M20 14c-2-4-7-4-7-1s5 1 7 1zm0 0c2-4 7-4 7-1s-5 1-7 1z' fill='none' stroke='%239ca3af' stroke-width='1.6'/%3E%3C/svg%3E"; // via.placeholder.com murió: NULL → bucle de error → parpadeo (24-ago-2026)

// --- INTERFACES ---
interface SuccessModalData {
  clientName: string | null;
  rewardTitle: string;
  rewardImage: string;
  pointsUsed: number;
  pointsBefore: number;
  pointsAfter: number;
}

// Cuenta el saldo de pointsBefore → pointsAfter al montar (plan 007): useCountUp
// no anima en el primer render, así que arrancamos en `from` y saltamos a `to`.
const BalanceCountUp: React.FC<{ from: number; to: number; format: (n: number) => string }> = ({ from, to, format }) => {
  const [v, setV] = useState(from);
  useEffect(() => {
    const t = setTimeout(() => setV(to), 200);
    return () => clearTimeout(t);
  }, [to]);
  return <CountUp value={v} duration={600} format={format} />;
};

// --- SUCCESS MODAL COMPONENT ---
const SuccessModal: React.FC<{
  data: SuccessModalData;
  loyaltyMode: 'visits' | 'cashback';
  onNewRedemption: () => void;
  onClose: () => void;
}> = ({ data, loyaltyMode, onNewRedemption, onClose }) => {

  const formatValue = (value: number) => {
    if (loyaltyMode === 'cashback') return `$${value.toFixed(2)}`;
    return `${Math.floor(value)} pts`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay oscuro */}
      <div className="modal-overlay absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Modal */}
      <div className="relative bg-surface rounded-2xl shadow-2xl max-w-md w-full p-6 modal-panel">
        {/* Icono de éxito */}
        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 bg-green-100 dark:bg-green-500/20 rounded-full flex items-center justify-center">
            <CheckCircle2 className="w-12 h-12 text-green-500 animate-pop" />
          </div>
        </div>

        {/* Título */}
        <h2 className="text-2xl font-bold text-content text-center mb-2">
          ¡Canje Exitoso!
        </h2>

        {/* Nombre del cliente */}
        <p className="text-content-2 text-center mb-6">
          Cliente: <span className="font-medium text-content">{data.clientName || 'Sin nombre'}</span>
        </p>

        {/* Recompensa canjeada */}
        <div className="bg-surface-2 rounded-xl p-4 mb-6">
          <div className="flex items-center space-x-4">
            <img 
              src={data.rewardImage || REWARD_IMG_FALLBACK} 
              alt={data.rewardTitle}
              className="w-16 h-16 rounded-lg object-cover bg-surface-2 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-content-3">Recompensa canjeada</p>
              <p className="font-semibold text-content truncate">{data.rewardTitle}</p>
              <p className="text-sm font-medium text-accent">{formatValue(data.pointsUsed)}</p>
            </div>
          </div>
        </div>

        {/* Línea de puntos */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-500/10 dark:to-green-500/10 rounded-xl p-4 mb-6">
          <p className="text-sm text-content-3 text-center mb-2">Balance actualizado</p>
          <div className="flex items-center justify-center space-x-3">
            <div className="text-center">
              <p className="text-lg font-bold text-content-3 line-through">{formatValue(data.pointsBefore)}</p>
              <p className="text-xs text-content-3">Antes</p>
            </div>
            <ArrowRight className="w-5 h-5 text-content-3" />
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                <BalanceCountUp from={data.pointsBefore} to={data.pointsAfter} format={formatValue} />
              </p>
              <p className="text-xs text-content-3">Restantes</p>
            </div>
          </div>
        </div>

        {/* Botones */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-line rounded-xl text-content-2 font-medium hover:bg-surface-2 transition-colors"
          >
            Cerrar
          </button>
          <button
            onClick={onNewRedemption}
            className="flex-1 px-4 py-3 bg-blue-600 rounded-xl text-white font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Canje
          </button>
        </div>
      </div>

    </div>
  );
};

// --- MAIN COMPONENT ---
const RedeemRewardsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { handleFirebaseError, clearErrors, addValidationError } = useError();

  // --- State Canje ---
  const [selectedClient, setSelectedClient] = useState<WhatsAppClient | null>(null);
  // Membresía F2 (20-oct-2026): lo que cubre el cupo del plan se canjea SIN puntos (el servidor lo descuenta del cupo).
  const [membership, setMembership] = useState<MembershipSummary | null>(null);
  const cargarMembresia = useCallback((id: number | string | undefined) => {
    setMembership(null);
    if (!id || !currentUser?.memberships) return;
    membershipsApi.forContact(Number(id)).then((f) => setMembership(f.summary || null)).catch(() => setMembership(null));
  }, [currentUser?.memberships]);
  useEffect(() => { cargarMembresia(selectedClient?.id); }, [selectedClient?.id, cargarMembresia]);
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [activeBranches, setActiveBranches] = useState<{ id: string, name: string }[]>([]);

  // --- State Modal de Éxito ---
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState<SuccessModalData | null>(null);

  // --- State Historial (Elasticsearch) ---
  const [historyItems, setHistoryItems] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historySearch, setHistorySearch] = useState('');
  const HISTORY_PAGE_SIZE = 10;

  const loyaltyMode = currentUser?.loyaltyMode || 'visits';

  const formatValue = (value: number) => {
    if (loyaltyMode === 'cashback') return `$${value.toFixed(2)}`;
    return `${Math.floor(value)} pts`;
  };

  // --- Efectos Iniciales ---
  useEffect(() => {
    const activeBusinessBranches = currentUser?.branches?.filter(branch => branch.isActive !== false) || [];
    setActiveBranches(activeBusinessBranches.map(branch => ({ id: branch.id, name: branch.name })));
    if (activeBusinessBranches.length === 1 && !selectedBranch) {
      setSelectedBranch(activeBusinessBranches[0].id);
    }
  }, [currentUser?.branches, selectedBranch]);

  // Al cambiar de sucursal, se suelta la recompensa elegida si es exclusiva de otra.
  useEffect(() => {
    if (selectedReward?.branchIds?.length && selectedBranch && !selectedReward.branchIds.includes(String(selectedBranch))) setSelectedReward(null);
  }, [selectedBranch, selectedReward]);

  // 1. Cargar Recompensas (Firebase)
  const fetchRewards = useCallback(async () => {
    if (!currentUser?.businessId) return;
    try {
      const all = await rewardsApi.list();
      const list = all.filter((r) => r.isActive).sort((a, b) => a.points - b.points);
      setRewards(list);
    } catch (e) { console.error('Error fetchRewards:', e); }
  }, [currentUser?.businessId]);

  // 2. Cargar Historial (Elasticsearch)
  const fetchHistory = useCallback(async () => {
      if (!currentUser?.businessId) return;
      setHistoryLoading(true);
      try {
          const data = await rewardsApi.history(historySearch, historyPage, HISTORY_PAGE_SIZE);
          setHistoryItems(data.items || []);
          setHistoryTotal(data.total || 0);
      } catch (error) {
          console.error("Error loading history:", error);
      } finally {
          setHistoryLoading(false);
      }
  }, [currentUser?.businessId, historyPage, historySearch]);

  useEffect(() => { fetchRewards(); }, [fetchRewards]);
  
  // Debounce para búsqueda en historial
  useEffect(() => { 
      const timeoutId = setTimeout(() => fetchHistory(), 300); 
      return () => clearTimeout(timeoutId);
  }, [fetchHistory]); 

  // --- Handlers ---

  // Handler cuando se SELECCIONA un cliente del dropdown
  const handleClientSelect = (result: SearchResultItem) => {
    console.log('Cliente seleccionado:', result);
    
    const clientData: WhatsAppClient = {
      id: result.id,
      phoneNumber: result.phone || '',
      visitorName: result.name || null,
      points: typeof result.points === 'number' ? result.points : 0,
      countryCode: result.countryCode || '',
      email: null,
      // El resultado de búsqueda no trae fechas; van en null en vez de inventar `new Date()`.
      createdAt: null,
      updatedAt: null,
      lastVisit: null,
      gender: '',
      businessId: currentUser?.businessId || '',
      birthDay: null,
      birthMonth: null,
    };
    
    setSelectedClient(clientData);
    setSelectedReward(null);
    clearErrors();
  };

  // Handler cuando se limpia el input de búsqueda
  const handleSearchClear = (term: string | null) => {
    if (!term) {
      setSelectedClient(null);
      setSelectedReward(null);
    }
  };

  // Devolver puntos de un canje que no se entregó (28-sep-2026). El cupón de la caja vive 15 min;
  // si nadie lo cobró, el cliente se quedaba sin puntos y sin premio. No es automático a propósito:
  // a veces la cortesía sí se entrega en caja por otra vía, y devolver solo regalaría los puntos.
  const [reversingId, setReversingId] = useState<string | null>(null);
  const handleReverse = async (item: any) => {
    const aviso = item.posTracked
      ? 'La caja NO registró este canje. ¿Confirmas que el cliente no recibió su premio y le devolvemos los puntos?'
      : '¿Confirmas que el cliente no recibió su premio y le devolvemos los puntos?';
    if (!window.confirm(`${item.visitorName || 'Cliente'} — ${item.rewardTitle}\n\n${aviso}`)) return;
    setReversingId(item.id);
    try {
      const r = await rewardsApi.reverse(item.id);
      window.alert(`Listo: se devolvieron ${r.points_returned} puntos. Saldo nuevo: ${r.balance}.`);
      fetchHistory();
    } catch (error: any) {
      const msg = error?.status === 403
        ? 'Solo el dueño o un administrador puede devolver puntos.'
        : (error?.message || 'No se pudo devolver el canje.');
      window.alert(msg);
    } finally {
      setReversingId(null);
    }
  };

  // Handler para cerrar modal y refrescar historial
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessModalData(null);
    setSelectedReward(null);
    // Refrescar historial
    setTimeout(() => fetchHistory(), 500);
  };

  // Handler para nuevo canje (limpia todo)
  const handleNewRedemption = () => {
    setShowSuccessModal(false);
    setSuccessModalData(null);
    setSelectedClient(null);
    setSelectedReward(null);
    // Refrescar historial
    setTimeout(() => fetchHistory(), 500);
  };

  const handleRedeem = async () => {
    if (!currentUser?.businessId || !selectedClient || !selectedReward) return;
    if (activeBranches.length === 0) { addValidationError?.('Sin sucursales', 'branchSelection'); return; }
    if (activeBranches.length > 1 && !selectedBranch) { addValidationError?.('Selecciona sucursal', 'branchSelection'); return; }
    
    const branchId = activeBranches.length === 1 ? activeBranches[0].id : selectedBranch!;

    // Guardar puntos antes del canje
    const pointsBefore = selectedClient.points ?? 0;

    setRedeemLoading(true);
    try {
      // Canje ATÓMICO server-side (valida saldo, descuenta y registra en el ledger).
      // El backend además dispara la alerta de saldo bajo, así que aquí no se maneja.
      const out = await clientsApi.redeem({ contactId: selectedClient.id, rewardIds: [selectedReward.id], branchId });
      const newPoints = typeof out.balance === 'number'
        ? out.balance
        : parseFloat((pointsBefore - selectedReward.points).toFixed(2));

      // Actualizar cliente en state
      setSelectedClient({ ...selectedClient, points: newPoints });

      // Preparar datos para el modal
      setSuccessModalData({
        clientName: selectedClient.visitorName,
        rewardTitle: selectedReward.title,
        rewardImage: selectedReward.imageUrl,
        pointsUsed: membershipCovers(membership, selectedReward.id) ? 0 : selectedReward.points,
        pointsBefore: pointsBefore,
        pointsAfter: newPoints
      });

      // Mostrar modal
      setShowSuccessModal(true);
      if (membership) cargarMembresia(selectedClient.id);   // cupo actualizado

    } catch (err) { handleFirebaseError(err, 'redeemReward'); } finally { setRedeemLoading(false); }
  };

  // --- RENDER ---

  return (
    <Layout>
      <div className="space-y-8">
        
        {/* HEADER */}
        <div className="animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          <h1 className="text-2xl font-bold text-content flex items-center">
            <Gift className="h-6 w-6 mr-2 text-accent" /> Canjear Recompensas
          </h1>
          <p className="mt-1 text-sm text-content-2">Gestiona canjes y visualiza el historial completo del negocio.</p>
        </div>

        {/* ZONA DE CANJE */}
        <div className="bg-surface shadow rounded-lg p-6 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
            <h3 className="text-lg font-medium text-content mb-4">Realizar Canje</h3>
            
            <div className="mb-6">
                {/* sinGatePro: aquí el buscador NO es una comodidad, es el único modo de
                    elegir al cliente. Con el gate puesto, un negocio en plan gratis entraba a
                    una pantalla que el menú le ofrece (Canjear Recompensas NO está marcada
                    como PRO) y no podía canjear, sin entender por qué — le pasó a EL BONSAI
                    (t990081) el 19-ago-2026. Además el canje ya es libre en el kiosco
                    (app.lealify.com), así que bloquearlo en el panel no defendía ingresos. */}
                <SearchInput 
                    placeholder="Buscar cliente (Teléfono o Nombre)" 
                    onSearch={handleSearchClear}
                    onClientSelect={handleClientSelect}
                    disabled={redeemLoading} 
                    preventClientNavigation={true}
                    sinGatePro={true}
                />
            </div>

            {selectedClient && (
                <div className="animate-fade-in border-t border-line pt-6">
                    {/* Info Cliente */}
                    <div className="bg-surface-2 p-4 rounded-lg border border-line mb-6 flex justify-between items-center">
                        <ClientContactInfo clientId={selectedClient.id} clientName={selectedClient.visitorName} phoneNumber={selectedClient.phoneNumber} countryCode={selectedClient.countryCode}/>
                        <div className="text-right">
                            <div className="text-xs text-content-3">Disponible</div>
                            <div className="text-xl font-bold text-green-600 dark:text-green-400">{formatValue(selectedClient.points || 0)}</div>
                        </div>
                    </div>

                    {/* Selector Sucursal */}
                    {activeBranches.length > 1 && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-content-2 mb-2">Sucursal</label>
                            <select value={selectedBranch || ''} onChange={(e) => setSelectedBranch(e.target.value)} className="block w-full border-line rounded-md shadow-sm focus:ring-accent focus:border-blue-500 sm:text-sm">
                                <option value="">Selecciona...</option>
                                {activeBranches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </div>
                    )}

                    {/* Grid Recompensas */}
                    <h4 className="text-sm font-medium text-content-2 mb-3">Selecciona Recompensa</h4>
                    {rewards.length === 0 ? (
                        <p className="text-sm text-content-3">No hay recompensas activas configuradas.</p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                            {rewards.map(reward => {
                                // Recompensa exclusiva (10-oct-2026): solo se canjea en sus sucursales. En el panel se ve siempre
                                // (es personal del negocio), apagada si la sucursal elegida no es una de las suyas.
                                const otraSucursal = !!reward.branchIds?.length && !!selectedBranch && !reward.branchIds.includes(String(selectedBranch));
                                const porCupo = membershipCovers(membership, reward.id);
                                const canAfford = (porCupo || (selectedClient.points || 0) >= reward.points) && !otraSucursal;
                                return (
                                    <div key={reward.id} onClick={() => canAfford && setSelectedReward(reward)} data-testid={`canje-recompensa-${reward.id}`}
                                        className={`p-3 rounded-lg border cursor-pointer transition-all flex items-center space-x-3 ${selectedReward?.id === reward.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 ring-1 ring-blue-500' : canAfford ? 'border-line hover:border-blue-300' : 'opacity-50 cursor-not-allowed bg-surface-2'}`}>
                                        <img src={reward.imageUrl || REWARD_IMG_FALLBACK} alt="" className="w-12 h-12 rounded object-cover bg-surface-2"/>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-content truncate">{reward.title}</p>
                                            <p className="text-sm font-bold text-accent">{porCupo ? <span data-testid="canje-por-cupo">🎟️ Incluido en su membresía</span> : formatValue(reward.points)}</p>
                                            {!!reward.branchNames?.length && <p className="text-[11px] text-content-3">Solo en {reward.branchNames.join(', ')}</p>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Botón Acción */}
                    <div className="flex justify-end">
                        <button onClick={handleRedeem} disabled={redeemLoading || !selectedReward} className="px-6 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors">
                            {redeemLoading ? 'Procesando...' : 'Confirmar Canje'}
                        </button>
                    </div>
                </div>
            )}
        </div>

        {/* HISTORIAL MEJORADO (Elasticsearch) */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{animationDelay: '0.15s'}}>
            <div className="p-6 border-b border-line flex flex-col sm:flex-row justify-between items-center gap-4">
                <h2 className="text-lg font-medium text-content flex items-center">
                    <HistoryIcon className="h-5 w-5 mr-2 text-content-3" /> Historial de Canjes
                </h2>
                
                {/* Buscador en Historial */}
                <div className="relative w-full sm:w-64">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-content-3" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-line rounded-md leading-5 bg-surface placeholder-content-3 focus:outline-none focus:placeholder-content-3 focus:ring-1 focus:ring-accent focus:border-blue-500 sm:text-sm"
                        placeholder="Buscar por nombre, tel o premio..."
                        value={historySearch}
                        onChange={(e) => { setHistorySearch(e.target.value); setHistoryPage(1); }}
                    />
                </div>
            </div>

            {/* Tabla */}
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-line">
                    <thead className="bg-surface-2">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Cliente</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Recompensa</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Valor</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Fecha</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Sucursal · atendió</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Estado</th>
                        </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-line">
                        {historyLoading ? (
                            <tr><td colSpan={6} className="px-6 py-8 text-center text-sm text-content-3"><div className="flex justify-center items-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div> Cargando historial...</div></td></tr>
                        ) : historyItems.length === 0 ? (
                            <tr><td colSpan={6} className="px-6 py-8 text-center text-sm text-content-3">No se encontraron canjes.</td></tr>
                        ) : (
                            historyItems.map((item) => (
                                <tr key={item.id} className="hover:bg-surface-2 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-8 w-8 bg-purple-100 dark:bg-purple-500/20 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-400 mr-3">
                                                <User className="h-4 w-4" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-medium text-content">{item.visitorName || 'Sin nombre'}</div>
                                                <div className="text-xs text-content-3">{item.visitorPhone}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-content font-medium">{item.rewardTitle || 'Recompensa'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${loyaltyMode === 'cashback' ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300' : 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300'}`}>
                                            {loyaltyMode === 'cashback' ? <DollarSign className="w-3 h-3 mr-1"/> : <Star className="w-3 h-3 mr-1"/>}
                                            {loyaltyMode === 'cashback' ? item.points.toFixed(2) : Math.floor(item.points)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-content-3">
                                        <div className="flex items-center">
                                            <CalendarClock className="w-4 h-4 mr-1.5 text-content-3"/>
                                            {new Date(item.redeemedAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })} 
                                            <span className="text-xs ml-1 text-content-3">
                                                {new Date(item.redeemedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-content-3">
                                        {item.branchName || '-'}
                                        {item.userName && <div className="text-xs text-content-3" data-testid="canje-usuario">{item.userName}</div>}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {item.status === 'reversed' ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-surface-2 text-content-3">Puntos devueltos</span>
                                        ) : item.posConsumedAt ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300">
                                                Cobrado en caja{item.posTicket ? ` · ${item.posTicket}` : ''}
                                            </span>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                {item.posTracked && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-300">Sin cobrar en caja</span>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() => handleReverse(item)}
                                                    disabled={reversingId === item.id}
                                                    className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                                                >
                                                    {reversingId === item.id ? 'Devolviendo…' : 'Devolver puntos'}
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            <div className="bg-surface px-4 py-3 border-t border-line flex items-center justify-between sm:px-6">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-content-2">
                            Mostrando <span className="font-medium">{historyTotal > 0 ? (historyPage - 1) * HISTORY_PAGE_SIZE + 1 : 0}</span> a <span className="font-medium">{Math.min(historyTotal, historyPage * HISTORY_PAGE_SIZE)}</span> de <span className="font-medium">{historyTotal}</span> resultados
                        </p>
                    </div>
                    <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                            <button
                                onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                                disabled={historyPage === 1 || historyLoading}
                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-line bg-surface text-sm font-medium text-content-3 hover:bg-surface-2 disabled:opacity-50"
                            >
                                <span className="sr-only">Anterior</span>
                                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                            </button>
                            <button
                                onClick={() => setHistoryPage(p => p + 1)}
                                disabled={historyPage * HISTORY_PAGE_SIZE >= historyTotal || historyLoading}
                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-line bg-surface text-sm font-medium text-content-3 hover:bg-surface-2 disabled:opacity-50"
                            >
                                <span className="sr-only">Siguiente</span>
                                <ChevronRight className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
        </div>

      </div>

      {/* MODAL DE ÉXITO */}
      {showSuccessModal && successModalData && (
        <SuccessModal
          data={successModalData}
          loyaltyMode={loyaltyMode}
          onNewRedemption={handleNewRedemption}
          onClose={handleCloseSuccessModal}
        />
      )}
    </Layout>
  );
};

export default RedeemRewardsPage;